"use client";

import { Search } from "lucide-react";
import Link from "@/components/ui/CcLink";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useBlogFilterNavigation } from "@/components/blog/CcBlogFilterNavigation";
import { resolveBlogCategorySlugForHref } from "@/lib/blog/blog-category-slug";
import { buildBlogQueryHref } from "@/lib/blog/blog-query-href";
import { categoryFromBlogPathname } from "@/lib/blog/category-routes";
import type { BlogListSort } from "@/lib/blog/list-filters";
import { formatCopy } from "@/lib/copy-format";
import { getBlogPageCopy } from "@/lib/blog-page-copy";
import type { Lang } from "@/lib/types";

export type BlogToolbarCategory = {
  slug: string;
  label: string;
};

type CcBlogToolbarProps = {
  lang: Lang;
  baseHref: string;
  categories: BlogToolbarCategory[];
};

function toolbarCopy(lang: Lang) {
  const copy = getBlogPageCopy(lang).toolbar;
  return {
    ...copy,
    results: (count: number) =>
      count === 1
        ? copy.resultsOne
        : formatCopy(copy.resultsMany, { count }),
  };
}

export function CcBlogToolbar({ lang, baseHref, categories }: CcBlogToolbarProps) {
  const text = toolbarCopy(lang);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isPending, resultsTotal } = useBlogFilterNavigation();

  const rawCategory = categoryFromBlogPathname(pathname, baseHref, lang) ?? searchParams.get("category") ?? "";
  const activeCategory = resolveBlogCategorySlugForHref(rawCategory) ?? "";
  const activeSort = (searchParams.get("sort") as BlogListSort | null) ?? "newest";
  const activeQuery = searchParams.get("q") ?? "";

  const [draftQuery, setDraftQuery] = useState(activeQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sortOptionRefs = useRef(new Map<BlogListSort, HTMLButtonElement>());
  const [sortIndicator, setSortIndicator] = useState({ left: 0, width: 0, ready: false });

  const sortOptions = useMemo(
    (): { value: BlogListSort; label: string }[] => [
      { value: "newest", label: text.sortNewest },
      { value: "oldest", label: text.sortOldest },
    ],
    [text.sortNewest, text.sortOldest],
  );

  const syncSortIndicator = useCallback(() => {
    const activeButton = sortOptionRefs.current.get(activeSort);
    if (!activeButton) {
      return;
    }
    setSortIndicator({
      left: activeButton.offsetLeft,
      width: activeButton.offsetWidth,
      ready: true,
    });
  }, [activeSort]);

  useLayoutEffect(() => {
    syncSortIndicator();
    window.addEventListener("resize", syncSortIndicator);
    return () => window.removeEventListener("resize", syncSortIndicator);
  }, [syncSortIndicator]);

  useEffect(() => {
    setDraftQuery(activeQuery);
  }, [activeQuery]);

  const pushParams = useCallback(
    (next: { q?: string; category?: string; sort?: BlogListSort; page?: number }) => {
      const href = buildBlogQueryHref(baseHref, {
        q: next.q ?? activeQuery,
        category:
          next.category !== undefined
            ? next.category || undefined
            : (resolveBlogCategorySlugForHref(rawCategory) ?? rawCategory) || undefined,
        sort: next.sort ?? activeSort,
        page: next.page ?? 1,
      });
      if (`${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}` === href) {
        return;
      }
      router.push(href, { scroll: false });
    },
    [rawCategory, activeQuery, activeSort, baseHref, pathname, router, searchParams],
  );

  const categoryHref = useCallback(
    (slug: string) =>
      buildBlogQueryHref(baseHref, {
        q: activeQuery || undefined,
        category: slug || undefined,
        sort: activeSort,
        page: 1,
      }),
    [activeQuery, activeSort, baseHref],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (draftQuery === activeQuery) return;

    debounceRef.current = setTimeout(() => {
      pushParams({ q: draftQuery, page: 1 });
    }, 320);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [draftQuery, activeQuery, pushParams]);

  const categoryTabs = useMemo(() => {
    const items = [{ slug: "", label: text.all }];
    for (const category of categories) {
      items.push({ slug: category.slug, label: category.label });
    }
    return items;
  }, [categories, text.all]);

  const categoryTabRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const handleCategoryTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
      const tabCount = categoryTabs.length;
      let nextIndex: number | null = null;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (index + 1) % tabCount;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (index - 1 + tabCount) % tabCount;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabCount - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      categoryTabRefs.current[nextIndex]?.focus();
    },
    [categoryTabs.length],
  );

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    pushParams({ q: draftQuery, page: 1 });
  };

  return (
    <section data-cc-nav="light" className="cc-blog-toolbar" aria-label={text.searchLabel}>
      <div className="cc-container cc-blog-toolbar__inner">
        <div className="cc-blog-toolbar__row cc-blog-toolbar__row--primary">
          <form data-analytics-form="blog_search" className="cc-blog-toolbar__search" onSubmit={onSearchSubmit}>
            <Search
              className="cc-blog-toolbar__search-icon"
              aria-hidden
              strokeWidth={2}
              size={18}
            />
            <input
              type="search"
              className="cc-contact__input cc-blog-toolbar__search-input"
              placeholder={text.searchPlaceholder}
              aria-label={text.searchLabel}
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
              autoComplete="off"
            />
          </form>
          <div className="cc-blog-toolbar__meta">
            <div className="cc-blog-toolbar__sort">
              <div
                className="cc-blog-toolbar__sort-rail"
                role="radiogroup"
                aria-label={text.sortLabel}
              >
                <span
                  className="cc-blog-toolbar__sort-indicator"
                  aria-hidden="true"
                  style={
                    {
                      "--cc-blog-sort-left": `${sortIndicator.left}px`,
                      "--cc-blog-sort-width": `${sortIndicator.width}px`,
                      opacity: sortIndicator.ready ? 1 : 0,
                    } as CSSProperties
                  }
                />
                {sortOptions.map((option) => {
                  const isActive = activeSort === option.value;
                  return (
                    <button
                      key={option.value}
                      ref={(node) => {
                        if (node) sortOptionRefs.current.set(option.value, node);
                        else sortOptionRefs.current.delete(option.value);
                      }}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      className="cc-blog-toolbar__sort-option"
                      onClick={() => {
                        if (isActive) return;
                        pushParams({ sort: option.value, page: 1 });
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <p
              className={`cc-blog-toolbar__count${isPending ? " cc-blog-toolbar__count--pending" : ""}${resultsTotal === null ? " cc-blog-toolbar__count--placeholder" : ""}`}
              aria-busy={isPending || undefined}
            >
              {resultsTotal === null ? null : text.results(resultsTotal)}
            </p>
          </div>
        </div>
        <div className="cc-blog-toolbar__categories">
          <div
            className="cc-faq__tabs"
            role="tablist"
            aria-label={text.categoriesLabel}
          >
            {categoryTabs.map((tab, index) => {
              const isActive = tab.slug === activeCategory;
              const tabId = tab.slug ? `cc-blog-category-${tab.slug}` : "cc-blog-category-all";

              return (
                <Link
                  key={tab.slug || "all"}
                  ref={(node) => {
                    categoryTabRefs.current[index] = node;
                  }}
                  href={categoryHref(tab.slug)}
                  scroll={false}
                  replace={false}
                  role="tab"
                  id={tabId}
                  className={`cc-faq__tab${isActive ? " is-active" : ""}`}
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  aria-current={isActive ? "page" : undefined}
                  onKeyDown={(event) => handleCategoryTabKeyDown(event, index)}
                >
                  {tab.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
